import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import CameraFileInput from '../components/CameraFileInput';
import { SHOW_INSTITUTO_BW_BRANDING } from '../lib/branding';
import { hasPermission } from '../lib/permissions';
import { appTodayDate } from '../lib/appDateTime';
import { 
  Clock, 
  UtensilsCrossed, 
  CheckCircle2, 
  AlertCircle, 
  AlertTriangle, 
  Circle,
  RefreshCw,
  MapPin,
  ExternalLink,
  Filter,
  XCircle,
  MinusCircle,
  Utensils,
  Syringe
} from 'lucide-react';

const ADMINISTRATION_TYPES = ['Voluntária', 'Forçada'];

export default function FeedingDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [groupedSchedules, setGroupedSchedules] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [summary, setSummary] = useState(null);
  const [bases, setBases] = useState([]);
  const [responsibleUsers, setResponsibleUsers] = useState([]);
  const [selectedDateMeta, setSelectedDateMeta] = useState({ isToday: true, isPastDate: false, isFutureDate: false });
  const canRecordFeeding = hasPermission(user, 'feeding.record');
  
  // Filters
  const [filterBase, setFilterBase] = useState('all');
  const [filterShift, setFilterShift] = useState('all');
  const [filterResponsible, setFilterResponsible] = useState('all');
  const [filterDate, setFilterDate] = useState(() => appTodayDate());
  
  // Record dialog
  const [showRecordDialog, setShowRecordDialog] = useState(false);
  const [recordingItem, setRecordingItem] = useState(null);
  const [recordNotes, setRecordNotes] = useState('');
  const [recordStatus, setRecordStatus] = useState('Aceita');
  const [recordAdministrationType, setRecordAdministrationType] = useState('Voluntária');
  const [recordResponsibleId, setRecordResponsibleId] = useState('');
  const [recordMediaFile, setRecordMediaFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Current time for live updates
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // Fetch bases on mount
  useEffect(() => {
    fetchBases();
  }, []);

  useEffect(() => {
    fetchResponsibleUsers();
  }, [filterBase]);

  useEffect(() => {
    if (filterResponsible !== 'all' && !responsibleUsers.some((responsible) => responsible.id === filterResponsible)) {
      setFilterResponsible('all');
    }
  }, [filterResponsible, responsibleUsers]);

  // Fetch dashboard data when filters change
  useEffect(() => {
    fetchDashboardData();
  }, [filterBase, filterShift, filterResponsible, filterDate]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const refreshTimer = setInterval(() => {
      fetchDashboardData(true);
    }, 300000);
    return () => clearInterval(refreshTimer);
  }, [filterBase, filterShift, filterResponsible, filterDate]);

  const fetchBases = async () => {
    try {
      const response = await axios.get(`${API}/bases`);
      setBases(response.data);
    } catch (error) {
      console.error('Error fetching bases:', error);
    }
  };

  const fetchResponsibleUsers = async () => {
    try {
      const params = {};
      if (filterBase && filterBase !== 'all') params.base_id = filterBase;
      const response = await axios.get(`${API}/feeding-responsibles`, { params });
      setResponsibleUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching feeding responsibles:', error);
    }
  };

  const getResponsibleName = (responsibleId) => {
    const responsible = responsibleUsers.find((item) => item.id === responsibleId);
    return responsible?.name || '';
  };

  const fetchDashboardData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    
    try {
      const params = new URLSearchParams();
      if (filterBase && filterBase !== 'all') params.append('base_id', filterBase);
      if (filterShift && filterShift !== 'all') params.append('shift', filterShift);
      if (filterResponsible && filterResponsible !== 'all') params.append('responsible_id', filterResponsible);
      if (filterDate) params.append('date', filterDate);
      
      const response = await axios.get(`${API}/feeding-dashboard?${params.toString()}`);
      setGroupedSchedules(response.data.grouped_schedules || []);
      setTimeSlots(response.data.time_slots || []);
      setSummary(response.data.summary || null);
      setSelectedDateMeta({
        isToday: response.data.is_today,
        isPastDate: response.data.is_past_date,
        isFutureDate: response.data.is_future_date
      });
    } catch (error) {
      toast.error('Erro ao carregar dashboard de alimentação');
      console.error('Error:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filterBase, filterShift, filterResponsible, filterDate]);

  const handleRecordClick = (animal, hour, schedule) => {
    if (schedule?.blocked) {
      toast.error(schedule.blocked_reason || `Horário bloqueado por jejum para ${schedule.blocked_procedure || 'procedimento'}`);
      return;
    }
    setRecordingItem({ 
      ...animal, 
      scheduled_hour: hour,
      schedule
    });
    setRecordNotes('');
    setRecordStatus('Aceita');
    setRecordAdministrationType('Voluntária');
    setRecordResponsibleId(schedule?.plan?.responsible_id || animal.responsible_id || user?.id || '');
    setRecordMediaFile(null);
    setShowRecordDialog(true);
  };

  const handleRecordSubmit = async () => {
    if (!recordingItem) return;
    
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/feeding-dashboard/quick-record?animal_id=${recordingItem.animal_id}&hour=${recordingItem.scheduled_hour}&date=${filterDate}&status=${recordStatus}&administration_type=${encodeURIComponent(recordAdministrationType)}`,
        {},
        {
          params: {
            notes: recordNotes || undefined,
            responsible_id: recordResponsibleId || undefined,
            responsible_name: getResponsibleName(recordResponsibleId) || undefined
          }
        }
      );

      let uploadedPhoto = false;
      let photoUploadFailed = false;
      if (recordMediaFile && response.data?.record_id) {
        const formData = new FormData();
        formData.append('file', recordMediaFile);
        formData.append('caption', 'Foto do alimento');
        try {
          await axios.post(`${API}/feeding-records/${response.data.record_id}/media`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          uploadedPhoto = true;
        } catch (uploadError) {
          photoUploadFailed = true;
          console.error('Error uploading feeding photo:', uploadError);
        }
      }
      
      if (photoUploadFailed) {
        toast.error('Alimentação registrada, mas a foto não foi enviada');
      } else {
        toast.success(uploadedPhoto ? 'Alimentação registrada com foto!' : 'Alimentação registrada!');
      }
      setShowRecordDialog(false);
      setRecordMediaFile(null);
      fetchDashboardData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar alimentação');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'done': return 'bg-green-100 text-green-700 border-green-300';
      case 'refused': return 'bg-red-100 text-red-700 border-red-300';
      case 'partial': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'upcoming': return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'overdue': return 'bg-orange-100 text-orange-700 border-orange-300';
      case 'blocked': return 'bg-slate-200 text-slate-700 border-slate-400';
      default: return 'bg-gray-100 text-gray-600 border-gray-300';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'done': return <CheckCircle2 className="w-4 h-4" />;
      case 'refused': return <XCircle className="w-4 h-4" />;
      case 'partial': return <MinusCircle className="w-4 h-4" />;
      case 'upcoming': return <AlertTriangle className="w-4 h-4" />;
      case 'overdue': return <AlertCircle className="w-4 h-4" />;
      case 'blocked': return <Circle className="w-4 h-4" />;
      default: return <Circle className="w-4 h-4" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'done': return 'Aceita';
      case 'refused': return 'Recusada';
      case 'partial': return 'Parcial';
      case 'upcoming': return 'Próximo';
      case 'overdue': return 'Atrasado';
      case 'blocked': return 'Bloqueado';
      default: return 'Pendente';
    }
  };

  const formatHour = (hour) => {
    return `${hour.toString().padStart(2, '0')}:00`;
  };

  const getGroupBadgeColor = (group) => {
    switch (group) {
      case 'AVE': return 'bg-sky-500 text-white';
      case 'MAM': return 'bg-amber-500 text-white';
      case 'REPT': return 'bg-green-600 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getFormIcon = (form) => {
    if (form === 'Assistida/Sonda') {
      return <Syringe className="w-3 h-3 text-blue-500" />;
    }
    return null;
  };

  const isCurrentHourSlot = (hour) => {
    if (!selectedDateMeta.isToday) return false;
    const now = currentTime.getHours();
    return now === hour;
  };

  // Calculate position for current time indicator
  const getCurrentTimePosition = () => {
    const now = currentTime.getHours();
    const minutes = currentTime.getMinutes();
    
    for (let i = 0; i < timeSlots.length; i++) {
      if (timeSlots[i] === now) {
        return { slotIndex: i, isAtSlot: true, minutesPast: minutes };
      }
      if (i < timeSlots.length - 1 && now > timeSlots[i] && now < timeSlots[i + 1]) {
        return { slotIndex: i, isAtSlot: false, hoursPast: now - timeSlots[i] };
      }
    }
    if (now >= timeSlots[timeSlots.length - 1]) {
      return { slotIndex: timeSlots.length - 1, isAtSlot: now === timeSlots[timeSlots.length - 1], minutesPast: minutes };
    }
    return { slotIndex: -1, isAtSlot: false };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
          <p className="mt-4 text-amber-700">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  const timePosition = getCurrentTimePosition();

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-amber-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="block md:hidden space-y-3">
            <div className="flex items-center space-x-3">
              {SHOW_INSTITUTO_BW_BRANDING && (
                <img
                  src="/bw-symbol-final.png"
                  alt="Instituto BW"
                  className="h-11 w-11 flex-shrink-0 object-contain"
                />
              )}
              <div className={`min-w-0 ${SHOW_INSTITUTO_BW_BRANDING ? 'border-l border-amber-200 pl-3' : ''}`}>
                <h1 className="break-words text-lg font-bold text-amber-800" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Dashboard de Alimentação
                </h1>
                <p className="text-xs text-amber-500">Rotina de Alimentação</p>
              </div>
            </div>
            <div className="space-y-2">
              <span className="flex items-center text-sm text-gray-600">
                <Clock className="w-4 h-4 mr-1" />
                {currentTime.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
              </span>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                <Button
                  onClick={() => fetchDashboardData(true)}
                  variant="outline"
                  size="sm"
                  disabled={refreshing}
                  className="w-full border-amber-600 text-amber-600"
                  data-testid="refresh-btn"
                >
                  <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                </Button>
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                  size="sm"
                  className="w-full border-amber-600 text-amber-600"
                  data-testid="back-dashboard-btn"
                >
                  Voltar
                </Button>
                <Button
                  onClick={logout}
                  variant="ghost"
                  size="sm"
                  className="w-full text-red-600 hover:bg-red-50"
                >
                  Sair
                </Button>
              </div>
            </div>
          </div>
          <div className="hidden md:flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {SHOW_INSTITUTO_BW_BRANDING && (
                <img
                  src="/bw-symbol-final.png"
                  alt="Instituto BW"
                  className="h-12 w-12 object-contain"
                />
              )}
              <div className={SHOW_INSTITUTO_BW_BRANDING ? 'border-l border-amber-200 pl-3' : ''}>
                <h1 className="text-lg md:text-xl font-bold text-amber-800" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Dashboard de Alimentação
                </h1>
                <p className="text-xs text-amber-500">Rotina de Alimentação</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className="flex items-center text-sm text-gray-600">
                <Clock className="w-4 h-4 mr-1" />
                {currentTime.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
              </span>
              <Button
                onClick={() => fetchDashboardData(true)}
                variant="outline"
                size="sm"
                disabled={refreshing}
                className="border-amber-600 text-amber-600"
                data-testid="refresh-btn-desktop"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                size="sm"
                className="border-amber-600 text-amber-600"
                data-testid="back-dashboard-btn-desktop"
              >
                Voltar
              </Button>
              <Button
                onClick={logout}
                variant="ghost"
                size="sm"
                className="text-red-600 hover:bg-red-50"
              >
                Sair
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-7 gap-3 mb-6">
            <Card className="bg-white border-amber-200">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-amber-700">{summary.total_animals}</div>
                <div className="text-xs text-gray-600">Animais</div>
              </CardContent>
            </Card>
            <Card className="bg-white border-amber-200">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-amber-700">{summary.total_feedings}</div>
                <div className="text-xs text-gray-600">Alimentações</div>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-green-600">{summary.completed}</div>
                <div className="text-xs text-gray-600">Aceitas</div>
              </CardContent>
            </Card>
            <Card className="bg-yellow-50 border-yellow-200">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-yellow-600">{summary.partial}</div>
                <div className="text-xs text-gray-600">Parciais</div>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-red-600">{summary.refused}</div>
                <div className="text-xs text-gray-600">Recusadas</div>
              </CardContent>
            </Card>
            <Card className="bg-gray-50 border-gray-200">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-gray-600">{summary.pending}</div>
                <div className="text-xs text-gray-600">Pendentes</div>
              </CardContent>
            </Card>
            <Card className="bg-slate-50 border-slate-200">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-slate-700">{summary.blocked || 0}</div>
                <div className="text-xs text-gray-600">Bloqueadas</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Filtros:</span>
              </div>
              <div className="flex-1 flex flex-wrap gap-4">
                <div className="w-48">
                  <Label className="text-xs text-gray-500">Data</Label>
                  <Input
                    type="date"
                    value={filterDate}
                    onChange={(e) => setFilterDate(e.target.value)}
                    data-testid="filter-date"
                  />
                </div>
                <div className="w-48">
                  <Label className="text-xs text-gray-500">Base</Label>
                  <Select value={filterBase} onValueChange={setFilterBase}>
                    <SelectTrigger data-testid="filter-base">
                      <SelectValue placeholder="Todas as bases" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas as bases</SelectItem>
                      {bases.map(base => (
                        <SelectItem key={base.id} value={base.id}>{base.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-48">
                  <Label className="text-xs text-gray-500">Turno</Label>
                  <Select value={filterShift} onValueChange={setFilterShift}>
                    <SelectTrigger data-testid="filter-shift">
                      <SelectValue placeholder="Todos os turnos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os turnos</SelectItem>
                      <SelectItem value="manha">Manhã (06h-12h)</SelectItem>
                      <SelectItem value="tarde">Tarde (12h-18h)</SelectItem>
                      <SelectItem value="noite">Noite (18h-06h)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-56">
                  <Label className="text-xs text-gray-500">Responsável</Label>
                  <Select value={filterResponsible} onValueChange={setFilterResponsible}>
                    <SelectTrigger data-testid="filter-responsible">
                      <SelectValue placeholder="Todos os responsáveis" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os responsáveis</SelectItem>
                      {responsibleUsers.map((responsible) => (
                        <SelectItem key={responsible.id} value={responsible.id}>
                          {responsible.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Schedule Grid */}
        {groupedSchedules.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <UtensilsCrossed className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-600">Nenhum plano alimentar encontrado</h3>
              <p className="text-sm text-gray-500 mt-2">
                Configure planos alimentares para os animais no prontuário.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-max">
                {/* Time slots header */}
                <thead className="bg-amber-50 border-b border-amber-200">
                  <tr>
                    <th className="w-64 px-4 py-3 text-left text-sm font-semibold text-gray-700 border-r border-amber-100">
                      Animal
                    </th>
                    {timeSlots.map((hour) => (
                      <th 
                        key={hour} 
                        className={`w-20 px-2 py-3 text-center text-sm font-semibold ${
                          isCurrentHourSlot(hour) 
                            ? 'bg-amber-500 text-white' 
                            : 'text-gray-700'
                        }`}
                      >
                        {formatHour(hour)}
                      </th>
                    ))}
                  </tr>
                </thead>
                
                {/* Animal rows */}
                <tbody className="divide-y divide-gray-100">
                  {groupedSchedules.map((animal) => (
                    <tr key={animal.animal_id} className="hover:bg-amber-50/30 transition-colors">
                      {/* Animal info */}
                      <td className="w-64 px-4 py-3 border-r border-gray-100 align-top">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={`${getGroupBadgeColor(animal.animal_group)} text-xs`}>
                            {animal.animal_group}
                          </Badge>
                          <div>
                            <Link 
                              to={`/animals/${animal.animal_id}/record`}
                              className="font-semibold text-amber-700 hover:text-amber-900 hover:underline flex items-center text-sm"
                            >
                              {animal.ibama_id}
                              <ExternalLink className="w-3 h-3 ml-1" />
                            </Link>
                            {animal.pmp_id && <p className="text-xs text-sky-700">PMP: {animal.pmp_id}</p>}
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 truncate">{animal.common_name}</p>
                        <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                          <MapPin className="w-3 h-3" />
                          <span className="truncate">{animal.allocation_location} - {animal.bay_number}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <Badge variant="outline" className="text-xs">
                            <Utensils className="w-3 h-3 mr-1" />
                            {animal.food_type}
                          </Badge>
                          {animal.responsible_name && (
                            <Badge variant="outline" className="text-xs">
                              {animal.responsible_name}
                            </Badge>
                          )}
                          {animal.form === 'Assistida/Sonda' && (
                            <Badge className="bg-blue-100 text-blue-700 text-xs">
                              <Syringe className="w-3 h-3 mr-1" />
                              Assistida
                            </Badge>
                          )}
                          {animal.schedules.some((item) => item.blocked) && (
                            <Badge className="bg-slate-100 text-slate-700 text-xs">
                              Jejum programado
                            </Badge>
                          )}
                        </div>
                      </td>

                      {/* Schedule cells */}
                      {timeSlots.map((hour) => {
                        const schedule = animal.schedules.find(s => s.hour === hour);
                        
                        if (!schedule) {
                          return (
                            <td 
                              key={hour} 
                              className={`w-20 px-2 py-3 text-center ${isCurrentHourSlot(hour) ? 'bg-amber-50' : 'bg-gray-50'}`}
                            >
                              <span className="text-gray-300">-</span>
                            </td>
                          );
                        }

                        return (
                          <td 
                            key={hour}
                            className={`w-20 px-2 py-3 ${isCurrentHourSlot(hour) ? 'bg-amber-50' : ''}`}
                          >
                            {canRecordFeeding ? (
                              <button
                                onClick={() => handleRecordClick(animal, hour, schedule)}
                                disabled={schedule.blocked}
                                className={`w-full min-h-[60px] rounded-lg border-2 flex flex-col items-center justify-center gap-1 transition-all ${schedule.blocked ? 'cursor-not-allowed opacity-70' : 'hover:scale-105 hover:shadow-md'} ${getStatusColor(schedule.status)}`}
                                title={schedule.blocked ? `${formatHour(hour)} - Bloqueado: ${schedule.blocked_procedure || 'jejum'}` : `${formatHour(hour)} - ${getStatusLabel(schedule.status)}`}
                                data-testid={`feeding-cell-${animal.animal_id}-${hour}`}
                              >
                                {getStatusIcon(schedule.status)}
                                <span className="text-xs font-medium">
                                  {getStatusLabel(schedule.status)}
                                </span>
                                {schedule.blocked && (
                                  <span className="text-[10px] opacity-80">Jejum</span>
                                )}
                                {schedule.records?.length > 0 && (
                                  <span className="text-[10px] opacity-70">
                                    {schedule.plan?.quantity}{schedule.plan?.unit}
                                  </span>
                                )}
                              </button>
                            ) : (
                              <div
                                className={`w-full min-h-[60px] rounded-lg border-2 flex flex-col items-center justify-center gap-1 ${getStatusColor(schedule.status)} cursor-default opacity-90`}
                                title={`${formatHour(hour)} - ${getStatusLabel(schedule.status)}`}
                                data-testid={`feeding-cell-${animal.animal_id}-${hour}`}
                              >
                                {getStatusIcon(schedule.status)}
                                <span className="text-xs font-medium">
                                  {getStatusLabel(schedule.status)}
                                </span>
                                {schedule.blocked && (
                                  <span className="text-[10px] opacity-80">Jejum</span>
                                )}
                                {schedule.records?.length > 0 && (
                                  <span className="text-[10px] opacity-70">
                                    {schedule.plan?.quantity}{schedule.plan?.unit}
                                  </span>
                                )}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Legend */}
        <Card className="mt-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 justify-center">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-green-200 border border-green-400"></div>
                <span className="text-sm text-gray-600">Aceita</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-yellow-200 border border-yellow-400"></div>
                <span className="text-sm text-gray-600">Parcial</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-red-200 border border-red-400"></div>
                <span className="text-sm text-gray-600">Recusada</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-blue-200 border border-blue-400"></div>
                <span className="text-sm text-gray-600">Próximo</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-orange-200 border border-orange-400"></div>
                <span className="text-sm text-gray-600">Atrasado</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-gray-200 border border-gray-400"></div>
                <span className="text-sm text-gray-600">Pendente</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-slate-200 border border-slate-400"></div>
                <span className="text-sm text-gray-600">Bloqueado por jejum</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Record Dialog */}
      <Dialog
        open={showRecordDialog}
        onOpenChange={(open) => {
          setShowRecordDialog(open);
          if (!open) setRecordMediaFile(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-800">
              <UtensilsCrossed className="w-5 h-5" />
              Registrar Alimentação
            </DialogTitle>
            <DialogDescription>
              {recordingItem && (
                <span>
                  {recordingItem.ibama_id} - {recordingItem.common_name} às {formatHour(recordingItem.scheduled_hour)}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {recordingItem && (
            <div className="space-y-4 pt-4">
              <div className="bg-amber-50 p-3 rounded-lg text-sm">
                <p><strong>Alimento:</strong> {recordingItem.schedule?.plan?.food_type}</p>
                <p><strong>Quantidade:</strong> {recordingItem.schedule?.plan?.quantity} {recordingItem.schedule?.plan?.unit}</p>
                <p><strong>Forma:</strong> {recordingItem.schedule?.plan?.form}</p>
                <p><strong>Responsável direcionado:</strong> {recordingItem.schedule?.plan?.responsible_name || 'Não definido'}</p>
                <p><strong>Data programada:</strong> {new Date(`${filterDate}T12:00:00`).toLocaleDateString('pt-BR')}</p>
              </div>

              {recordingItem.schedule?.blocked && (
                <div className="rounded-lg border border-slate-300 bg-slate-100 p-3 text-sm text-slate-700">
                  <p><strong>Horário bloqueado</strong></p>
                  <p>{recordingItem.schedule?.blocked_procedure || 'Jejum programado'}</p>
                  {recordingItem.schedule?.blocked_reason && <p>{recordingItem.schedule.blocked_reason}</p>}
                </div>
              )}

              <div>
                <Label className="mb-2 block">Tipo de Administração</Label>
                <div className="grid grid-cols-2 gap-2">
                  {ADMINISTRATION_TYPES.map((value) => (
                    <button
                      key={value}
                      onClick={() => setRecordAdministrationType(value)}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        recordAdministrationType === value
                          ? 'bg-amber-100 border-amber-500 text-amber-800 ring-2 ring-offset-2 ring-amber-300'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`administration-type-${value.toLowerCase()}`}
                    >
                      <span className="font-medium">{value}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <Label className="mb-2 block">Status da Alimentação</Label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'Aceita', color: 'bg-green-100 border-green-500 text-green-700', icon: CheckCircle2 },
                    { value: 'Parcial', color: 'bg-yellow-100 border-yellow-500 text-yellow-700', icon: MinusCircle },
                    { value: 'Recusada', color: 'bg-red-100 border-red-500 text-red-700', icon: XCircle },
                    { value: 'Oferecida', color: 'bg-gray-100 border-gray-500 text-gray-700', icon: Circle }
                  ].map(({ value, color, icon: Icon }) => (
                    <button
                      key={value}
                      onClick={() => setRecordStatus(value)}
                      className={`p-3 rounded-lg border-2 flex items-center gap-2 transition-all ${
                        recordStatus === value 
                          ? color + ' ring-2 ring-offset-2 ring-amber-400' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`status-${value.toLowerCase()}`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{value}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <Label>Responsável direcionado</Label>
                <Select value={recordResponsibleId || 'none'} onValueChange={(value) => setRecordResponsibleId(value === 'none' ? '' : value)}>
                  <SelectTrigger data-testid="feeding-responsible-select">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Não definido</SelectItem>
                    {responsibleUsers.map((responsible) => (
                      <SelectItem key={responsible.id} value={responsible.id}>
                        {responsible.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  className="mt-2"
                  value={user?.full_name || user?.name || user?.email || ''}
                  disabled
                  aria-label="Executado por"
                  data-testid="feeding-responsible-input"
                />
              </div>

              <div>
                <Label>Observações (opcional)</Label>
                <Textarea
                  value={recordNotes}
                  onChange={(e) => setRecordNotes(e.target.value)}
                  placeholder="Observações sobre a alimentação..."
                  rows={3}
                  data-testid="record-notes"
                />
              </div>

              <div>
                <Label>Foto do alimento (opcional)</Label>
                <CameraFileInput
                  accept="image/*"
                  disabled={submitting}
                  onFileSelected={setRecordMediaFile}
                  fileLabel="Selecionar foto"
                  cameraLabel="Tirar foto"
                  data-testid="feeding-dashboard-photo-input"
                />
                {recordMediaFile && (
                  <p className="mt-1 text-xs text-gray-500">Selecionado: {recordMediaFile.name}</p>
                )}
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowRecordDialog(false);
                    setRecordMediaFile(null);
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleRecordSubmit}
                  disabled={submitting || recordingItem.schedule?.blocked}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="submit-record-btn"
                >
                  {submitting ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  Registrar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
